import { useCallback } from 'react'
import { type RouterOutputs, trpc } from '../../../../lib/trpc.js'

/**
 * The list row as the router actually returns it. Derived, not hand-written:
 * the shapes these replaced were re-declared by hand and then asserted onto the
 * query result, so a router change (a renamed field, a widened enum, a column
 * that became nullable) type-checked clean here and only surfaced at runtime.
 */
export type SystemDesignProblem =
  RouterOutputs['admin']['content']['listSystemDesignProblems']['data'][number]

export type SystemDesignProblemDifficulty = NonNullable<
  SystemDesignProblem['difficulty']
>

export type SystemDesignCompanyTag = SystemDesignProblem['companyTags'][number]

export function useSystemDesignProblems() {
  const utils = trpc.useUtils()

  const { data, isLoading, error } =
    trpc.admin.content.listSystemDesignProblems.useQuery()

  const deleteMutation =
    trpc.admin.content.deleteSystemDesignProblem.useMutation({
      onSuccess: () => {
        utils.admin.content.listSystemDesignProblems.invalidate()
      },
    })

  const deleteProblem = useCallback(
    async (id: string): Promise<void> => {
      await deleteMutation.mutateAsync({ id })
    },
    [deleteMutation],
  )

  const refresh = useCallback(async () => {
    await utils.admin.content.listSystemDesignProblems.invalidate()
  }, [utils])

  return {
    problems: data?.data ?? [],
    loading: isLoading,
    error: error ? error.message : null,
    refresh,
    deleteProblem,
  }
}
